import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { permissionsService, PermissionsData, FeaturePermissions } from '../services/permissionsService';

interface PermissionsState {
  // State
  permissions: PermissionsData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  
  // Actions
  loadPermissions: (organizationId: string, forceRefresh?: boolean) => Promise<void>;
  hasFeature: (featureName: keyof FeaturePermissions) => boolean;
  clearPermissions: () => void;
  clearError: () => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

const usePermissionsStore = create<PermissionsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      permissions: null,
      loading: false,
      error: null,
      lastUpdated: null,

      // Actions
      loadPermissions: async (organizationId: string, forceRefresh = false) => {
        const { permissions, lastUpdated, loading } = get();
        
        // Skip if already loading
        if (loading) return;
        
        // Skip if we have fresh cached data and not forcing refresh
        if (!forceRefresh && permissions && lastUpdated && 
            (Date.now() - lastUpdated < CACHE_DURATION) &&
            permissions.organizationId === organizationId) {
          return;
        }

        set({ loading: true, error: null });

        try {
          const response = await permissionsService.getFeaturePermissions(organizationId);
          
          if (response.success && response.data) {
            set({
              permissions: response.data,
              loading: false,
              error: null,
              lastUpdated: Date.now(),
            });
          } else {
            const errorMessage = response.error?.message || 'Failed to load permissions';
            // Keep the last-known permissions. Nulling them makes `permissionsLoaded`
            // (!!permissions) false downstream, which silently drops feature gating —
            // e.g. BrandScoutPage then renders a raw error instead of its
            // "not enabled for your organization" card.
            set({
              loading: false,
              error: errorMessage,
            });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load permissions';
          console.error('Error loading permissions:', error);
          // Preserve the last-known permissions — a transient failure (429 from the auth
          // service's per-IP rate limiter, network blip) must not erase entitlements we
          // already resolved. The backend still enforces access with its own 403, so this
          // only affects what the UI shows.
          set({
            loading: false,
            error: errorMessage,
          });
        }
      },

      hasFeature: (featureName: keyof FeaturePermissions) => {
        const { permissions } = get();
        if (!permissions || !permissions.features) {
          return false;
        }
        return permissions.features[featureName] === true;
      },

      clearPermissions: () => {
        set({
          permissions: null,
          loading: false,
          error: null,
          lastUpdated: null,
        });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'permissions-store',
    }
  )
);

export default usePermissionsStore;